<!--
 * @Author: 张天昊
 * @Date: 2024-05-21 16:44:19
 * @LastEditTime: 2024-07-02 13:46:45
 * @LastEditors: 张天昊
 * @Description: 行程信息面板
 * @FilePath: /Mobile/src/pages/itineraryUniversal/components/ItineraryInfoPanel.vue
-->
<script>
import { defineComponent, computed, ref } from 'vue';
import moment from 'moment';
import SpotListBar from './SpotListBar.vue';
import BlockWithTitle from './BlockWithTitle.vue';
import ServiceCard from './ServiceCard.vue';
import ScenicCard from './ScenicCard.vue';
import HotelCard from './HotelCard.vue';
import CateringCard from './CateringCard.vue';
import DataEmpty from './DataEmpty.vue';
import { usePanelMinHeight } from '../composables/usePanelMinHeight';
import store from '@/store';
import {
  AuxiliaryInfoTypes,
  dateStrFormat1,
  dateStrFormat2,
  dateStrFormat3
} from '../constants';

export default defineComponent({
  components: {
    SpotListBar,
    BlockWithTitle,
    ScenicCard,
    HotelCard,
    CateringCard,
    ServiceCard,
    DataEmpty
  },
  setup(_, { emit }) {
    const detailInfo = computed(
      () => store.state.itineraryUniversal.detailInfo.value
    );
    const current = ref(-1);
    const spotLen = computed(() =>
      detailInfo.value ? detailInfo.value.itineraryStateMessage.length : 0
    );
    const currentSpot = computed(() =>
      detailInfo.value
        ? detailInfo.value.itineraryStateMessage[current.value]
        : null
    );
    const currentSpotService = computed(() => {
      if (!currentSpot.value) {
        return;
      }
      return {
        [AuxiliaryInfoTypes.导游]: currentSpot.value.stateGuideVOList.map(
          (item) => ({
            id: item.guideId,
            photoPath: item.photoPath,
            name: `${item.name}（${item.certificate}）`
          })
        ),
        [AuxiliaryInfoTypes.车辆]: currentSpot.value.stateCarList.map(
          (item) => ({
            id: item.trafficId,
            name: `${item.carPlate}（${item.driverName}）`
          })
        ),
        // 接口没返旅行社id
        [AuxiliaryInfoTypes.旅行社]: currentSpot.value.localTravelNum
          ? currentSpot.value.travelList.map((item) => ({
              id: item.travelId || item.travelName,
              name: item.travelName
            }))
          : {
              id: currentSpot.value.travelId || currentSpot.value.travelName,
              name: currentSpot.value.travelName
            },
        tourists: currentSpot.value.stateTouristList.map(
          (item) => item.touristId
        ),
        time: [
          moment(currentSpot.value.startTime).format(dateStrFormat1),
          moment(currentSpot.value.endTime).format(dateStrFormat1)
        ]
      };
    });
    /**
     * 景区
     */
    const currentSpotScenic = computed(() => {
      if (!currentSpot.value) {
        return;
      }
      return currentSpot.value.stateScenicList.map((item) => {
        let playtimeContent;
        if (!item.activityTimeStart) {
          playtimeContent = '--';
        } else {
          const start = moment(item.activityTimeStart);
          playtimeContent = start.format(dateStrFormat3);

          switch (item.playTime) {
            case 0:
              playtimeContent += ' 全天';
              break;
            case 1:
              playtimeContent += ' 早上';
              break;
            case 2:
              playtimeContent += ' 中午';
              break;
            case 3:
              playtimeContent += ' 下午';
              break;
            case 4:
              playtimeContent += ' 晚上';
              break;
            default:
              if (item.activityTimeEnd) {
                playtimeContent += ` ${moment(item.activityTimeEnd).diff(
                  start,
                  'hours',
                  true
                ).toFixed(2)}小时`;
              }
              break;
          }
        }

        return {
          title: item.scenicName,
          playtime: playtimeContent,
          tourists: item.touristIds,
          location: item.gotoCountiesName
        };
      });
    });
    /**
     * 酒店
     */
    const currentSpotHotel = computed(() => {
      if (!currentSpot.value) {
        return;
      }
      return currentSpot.value.stateHotelList.map((item) => {
        const meal = [
          { label: '早', value: item.isBreakfast },
          { label: '中', value: item.isLunch },
          { label: '晚', value: item.isDinner }
        ]
          .filter((m) => m.value)
          .map((m) => m.label)
          .join('/');
        return {
          title: item.hotelName,
          from: moment(item.startDate).format(dateStrFormat2),
          to: moment(item.endDate).format(dateStrFormat2),
          catering: meal ? `含${meal}` : '无餐食'
        };
      });
    });
    /**
     * 餐食
     */
    const currentSpotCatering = computed(() => {
      if (!currentSpot.value) {
        return;
      }
      return currentSpot.value.stateDinningList.map((item) => {
        return {
          title: moment(item.date).format(dateStrFormat3),
          breakfast: item.breakfast,
          lunch: item.lunch,
          dinner: item.dinner
        };
      });
    });
    function handleServiceCardLink({ type, id }) {
      store.commit('itineraryUniversal/setAnchorTarget', { type, id });
      emit('link', type);
    }

    const { panelMinHeight } = usePanelMinHeight();
    return {
      detailInfo,
      spotLen,
      currentSpot,
      currentSpotService,
      currentSpotScenic,
      currentSpotHotel,
      currentSpotCatering,
      current,
      handleServiceCardLink,
      panelMinHeight
    };
  }
});
</script>

<template>
  <div :style="{ minHeight: panelMinHeight }">
    <!-- 可能也要fixed -->
    <div v-if="spotLen" class="bg-white px-12 pb-16">
      <SpotListBar v-model="current" />
    </div>
    <div v-if="currentSpot" class="px-12">
      <BlockWithTitle class="mt-20" title="站点服务">
        <ServiceCard :data="currentSpotService" @link="handleServiceCardLink" />
      </BlockWithTitle>
      <BlockWithTitle class="mt-20" title="站点景区">
        <ScenicCard
          v-if="currentSpotScenic && currentSpotScenic.length"
          :data="currentSpotScenic"
        />
        <DataEmpty v-else size="small" />
      </BlockWithTitle>
      <BlockWithTitle class="mt-20" title="入住酒店">
        <HotelCard
          v-if="currentSpotHotel && currentSpotHotel.length"
          :data="currentSpotHotel"
        />
        <DataEmpty v-else size="small" />
      </BlockWithTitle>
      <BlockWithTitle class="mt-20" title="餐饮情况">
        <CateringCard
          v-if="currentSpotCatering && currentSpotCatering.length"
          :data="currentSpotCatering"
        />
        <DataEmpty v-else size="small" />
      </BlockWithTitle>
    </div>
    <div v-else class="px-12">
      <DataEmpty class="mt-16" />
    </div>
  </div>
</template>
